import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FC, useCallback, useMemo } from 'react';

import { keys } from 'app/config/keys';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { PropsWithOnlyChildren } from 'app/types';
import { handleErrors } from 'app/utils/lib/context';

const staleTime = keys.NODE_ENV === 'test'
  ? 500 // short times to prevent tests from hanging
  : 300000;

const ReactQueryOptionSetter: FC<PropsWithOnlyChildren> = ({ children }) => {
  const alertsDispatch = useAlertsDispatch();

  const queryClient = useQueryClient();

  const handleError = useCallback((errorObject: Error) => {
    handleErrors({
      dispatch: alertsDispatch,
      errorObject,
    });
  },
  [alertsDispatch]);

  queryClient.setDefaultOptions({
    mutations: {
      onError: handleError,
    },
    queries: {
      cacheTime: 300000,
      onError: handleError,
      refetchOnWindowFocus: false,
      retry: 0,
      staleTime,
    },
  });

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const ReactQueryProvider: FC<PropsWithOnlyChildren> = ({ children }) => {
  // It is important to memoize the queryClient so that it is not recreated on every render
  // We should make every effort to keep the dependencies of this useMemo as minimal as possible
  // This is why we have the ReactQueryOptionSetter, which depends on alertsDispatch
  const queryClient = useMemo<QueryClient>(() => {
    return new QueryClient();
  },
  []);

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryOptionSetter>
        {children}
      </ReactQueryOptionSetter>

      {keys.__DEV__ && !keys.HIDE_REACT_QUERY_DEV_TOOLS && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
};

export default ReactQueryProvider;
